/**
 * BlogHome — Public blog homepage (blog.example.com)
 * Hero featured post + grid of recent posts + sidebar
 */

import { T } from '../styles/tokens';
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Button } from '../components/ui';

const API_BASE = '/api/v1/cms';

interface Post {
  id: string; slug: string; author_name: string; author_avatar: string; is_featured: number;
  cover_image: string; view_count: number; like_count: number;
  tags: string; metadata: string; published_at: string; title: string; excerpt: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
}
interface Category { id: string; slug: string; icon: string; color: string; name: string; description: string; entry_count: number; }
interface Tag { name: string; count: number; }

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return navigator.language?.startsWith('pt') ? 'pt-BR' : 'en';
}

function formatDate(d: string, locale: string): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
}

function parseTags(t: string): string[] {
  try { return JSON.parse(t || '[]'); } catch { return []; }
}

// ── Styles ──
const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  hero: { position: 'relative' as const, borderRadius: 16, overflow: 'hidden', marginBottom: 48, minHeight: 400, display: 'flex', alignItems: 'flex-end', cursor: 'pointer', transition: 'transform 0.2s', background: 'linear-gradient(135deg, #1a1e3a 0%, #0a0e1a 100%)' } as any,
  heroOverlay: { position: 'absolute' as const, inset: 0, background: 'linear-gradient(180deg, transparent 0%, rgba(10,14,26,0.8) 70%, #0a0e1a 100%)' } as any,
  heroContent: { position: 'relative' as const, padding: '40px 32px', zIndex: 1 } as any,
  heroTitle: { fontSize: 36, fontWeight: 800, lineHeight: 1.2, margin: '12px 0 16px', textShadow: `0 2px 20px ${T.blackAlpha50}` } as any,
  heroExcerpt: { fontSize: 16, color: T.textSecondary, lineHeight: 1.6, maxWidth: 600 } as any,
  heroCta: { display: 'inline-block', marginTop: 20, padding: '12px 28px', background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)', color: T.text, borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none', border: 'none', cursor: 'pointer' } as any,
  layout: { display: 'flex', gap: 40 } as any,
  main: { flex: 1, minWidth: 0 } as any,
  sidebar: { width: 300, flexShrink: 0 } as any,
  sectionTitle: { fontSize: 20, fontWeight: 700, marginBottom: 24 } as any,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340, 1fr))', gap: 24, marginBottom: 40 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s' } as any,
  cardImg: { width: '100%', aspectRatio: '16/9', objectFit: 'cover' as const, display: 'block' } as any,
  cardImgPlaceholder: { width: '100%', aspectRatio: '16/9', background: 'linear-gradient(135deg, #1a1e3a 0%, #12162b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.bgSecondary, fontSize: 48 } as any,
  cardBody: { padding: '16px 20px 20px' } as any,
  cardTitle: { fontSize: 17, fontWeight: 700, lineHeight: 1.3, margin: '8px 0', color: T.text } as any,
  cardExcerpt: { fontSize: 14, color: T.textSecondary, lineHeight: 1.5, margin: '0 0 12px' } as any,
  cardMeta: { display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: T.textMuted } as any,
  badge: (color: string) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}20`, color }) as any,
  readTime: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: T.textMuted } as any,
  sidebarCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20 } as any,
  sidebarTitle: { fontSize: 14, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: T.textSecondary } as any,
  catItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', fontSize: 14 } as any,
  catDot: (color: string) => ({ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }) as any,
  catCount: { marginLeft: 'auto', fontSize: 12, color: T.textMuted } as any,
  tagPill: { display: 'inline-block', padding: '4px 10px', borderRadius: 16, fontSize: 12, background: T.border, color: T.textSecondary, cursor: 'pointer', margin: '0 6px 6px 0' } as any,
  loadMore: { display: 'block', width: '100%', padding: '12px', background: T.bgCardHover, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textSecondary, fontSize: 14, cursor: 'pointer', textAlign: 'center' as const, marginBottom: 40 } as any,
  skeleton: { background: `linear-gradient(90deg, ${T.whiteAlpha3} 25%, ${T.border} 50%, ${T.bgCard} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 8 } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 } as any,
  nav: { display: 'flex', gap: 16 } as any,
  navLink: { color: T.textSecondary, textDecoration: 'none', fontSize: 14 } as any,
};

function PostCard({ post, locale }: { post: Post; locale: string }) {
  return (
    <div style={S.card} onClick={() => { window.location.href = `/${locale}/${post.slug}`; }}
      onMouseOver={(e: any) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.borderColor = T.primaryBg; }}
      onMouseOut={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = T.border; }}>
      {post.cover_image ? <img style={S.cardImg} src={post.cover_image} alt={post.title} loading="lazy" /> : <div style={S.cardImgPlaceholder}>{post.category_icon === 'brain' ? '\u25CB' : '\u25A1'}</div>}
      <div style={S.cardBody}>
        {post.category_name && <span style={S.badge(post.category_color || T.primary)}>{post.category_name}</span>}
        <div style={S.cardTitle}>{post.title}</div>
        <p style={S.cardExcerpt}>{post.excerpt?.substring(0, 150)}{(post.excerpt?.length || 0) > 150 ? '...' : ''}</p>
        <div style={S.cardMeta}>
          <span>{post.author_name || ''}</span>
          <span>{formatDate(post.published_at, locale)}</span>
          <span style={S.readTime}>{(() => { try { const m = JSON.parse(post.metadata || '{}'); return m.reading_time_min || 5; } catch { return 5; } })()} min</span>
        </div>
      </div>
    </div>
  );
}

export function BlogHome(_props: { path?: string; locale?: string }) {
  const locale = _props.locale || getLocale();
  const [posts, setPosts] = useState<Post[]>([]);
  const [featured, setFeatured] = useState<Post | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/entries?contentType=blog&status=published&featured=1&locale=${locale}&limit=1`).then(r => r.json()),
      fetch(`${API_BASE}/entries?contentType=blog&status=published&locale=${locale}&limit=20&offset=0`).then(r => r.json()),
      fetch(`${API_BASE}/categories?contentType=blog&locale=${locale}`).then(r => r.json()),
    ]).then(([featRes, postsRes, catRes]) => {
      const feat = featRes.data?.entries?.[0] || null;
      setFeatured(feat);
      const allPosts = postsRes.data?.entries || [];
      setPosts(feat ? allPosts.filter((p: Post) => p.id !== feat.id) : allPosts);
      setTotal(postsRes.data?.total || 0);
      setOffset(20);
      setCategories(catRes.data?.categories || []);
      setTags([]);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [locale]);

  const loadMore = () => {
    fetch(`${API_BASE}/entries?contentType=blog&status=published&locale=${locale}&limit=20&offset=${offset}`).then(r => r.json()).then(res => {
      setPosts(prev => [...prev, ...(res.data?.entries || [])]);
      setOffset(prev => prev + 20);
    });
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={{ ...S.skeleton, height: 400, marginBottom: 40 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            {[1, 2, 3, 4].map(i => <div key={i} style={{ ...S.skeleton, height: 300 }} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } } @media (max-width: 768px) { .blog-layout { flex-direction: column !important; } .blog-sidebar { width: 100% !important; } .blog-grid { grid-template-columns: 1fr !important; } }`}</style>
      <div style={S.container}>
        {/* Header */}
        <div style={S.header}>
          <a href={`/${locale}`} style={S.logo}>
            <span style={S.logoAccent}>WhiteLabelClaw</span>
            <span style={{ color: T.textMuted, fontWeight: 400 }}>Blog</span>
          </a>
          <nav style={S.nav}>
            {categories.slice(0, 4).map(cat => (
              <a key={cat.slug} href={`/${locale}/${cat.slug}`} style={S.navLink}>{cat.name}</a>
            ))}
          </nav>
        </div>

        {/* Hero */}
        {featured && (
          <div style={{ ...S.hero, backgroundImage: featured.cover_image ? `url(${featured.cover_image})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }}
            onClick={() => { window.location.href = `/${locale}/${featured.slug}`; }}>
            <div style={S.heroOverlay} />
            <div style={S.heroContent}>
              {featured.category_name && <span style={S.badge(featured.category_color || T.primary)}>{featured.category_name}</span>}
              <h1 style={S.heroTitle}>{featured.title}</h1>
              <p style={S.heroExcerpt}>{featured.excerpt}</p>
              <span style={S.heroCta}>{locale === 'pt-BR' ? 'Ler artigo' : 'Read article'}</span>
            </div>
          </div>
        )}

        {/* Layout */}
        <div class="blog-layout" style={S.layout}>
          <div style={S.main}>
            <h2 style={S.sectionTitle}>{locale === 'pt-BR' ? 'Artigos Recentes' : 'Recent Articles'}</h2>
            <div class="blog-grid" style={S.grid}>
              {posts.map(post => <PostCard key={post.id} post={post} locale={locale} />)}
            </div>
            {posts.length + (featured ? 1 : 0) < total && (
              <Button variant="ghost" onClick={loadMore} fullWidth style={S.loadMore}>{locale === 'pt-BR' ? 'Carregar mais' : 'Load more'}</Button>
            )}
          </div>

          {/* Sidebar */}
          <div class="blog-sidebar" style={S.sidebar}>
            <div style={S.sidebarCard}>
              <div style={S.sidebarTitle}>{locale === 'pt-BR' ? 'Categorias' : 'Categories'}</div>
              {categories.map(cat => (
                <div key={cat.slug} style={S.catItem} onClick={() => { window.location.href = `/${locale}/${cat.slug}`; }}>
                  <span style={S.catDot(cat.color)} />
                  <span>{cat.name}</span>
                  <span style={S.catCount}>{cat.entry_count}</span>
                </div>
              ))}
            </div>
            <div style={S.sidebarCard}>
              <div style={S.sidebarTitle}>Tags</div>
              <div style={{ display: 'flex', flexWrap: 'wrap' as const }}>
                {tags.slice(0, 20).map(tag => (
                  <span key={tag.name} style={S.tagPill} onClick={() => { window.location.href = `/${locale}?tag=${tag.name}`; }}>{tag.name} ({tag.count})</span>
                ))}
              </div>
            </div>
            <div style={S.sidebarCard}>
              <div style={S.sidebarTitle}>Newsletter</div>
              <p style={{ fontSize: 13, color: T.textSecondary, margin: '0 0 12px' }}>{locale === 'pt-BR' ? 'Receba as ultimas noticias sobre IA' : 'Get the latest AI news'}</p>
              <input type="email" placeholder="email@exemplo.com" style={{ width: '100%', padding: '10px 12px', background: T.bgCardHover, border: `1px solid ${T.borderHover}`, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, marginBottom: 8 }} />
              <Button variant="primary" fullWidth size="sm" style={{ textAlign: 'center' as const }}>{locale === 'pt-BR' ? 'Inscrever-se' : 'Subscribe'}</Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <p>{locale === 'pt-BR' ? '2026 WhiteLabelClaw. Todos os direitos reservados.' : '2026 WhiteLabelClaw. All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}
